import { useCallback, useEffect, useState } from 'react'
import { LIVE_CARD_MAX_TAGS } from '@/components/live/LiveSeriesCard'
import type { Plan, RunState } from '../v4/contracts'

/** One live card the user added to a run: a live time-series document and the tags it shows. */
export interface LiveCardSpec {
  /** `documentId` and tags, so adding the same card twice is one card. */
  id: string
  documentId: string
  tags: string[]
  /** The step it was added from, as its heading. */
  title: string
  /** That step's id, so the canvas can place the card beside it (cards saved before this have none). */
  stepId?: string
}

/** The series a step read from one document: what one "Add live card" click adds. */
export interface StepSeries {
  documentId: string
  tags: string[]
}

type Step = Plan['steps'][number]

/**
 * The series a `read_series` step reads, grouped by the document each was read from (the run
 * state's `seriesDocuments`, the tag catalogue's own mapping). A tag the run state cannot place is
 * left out, and a group keeps at most LIVE_CARD_MAX_TAGS tags. Empty for any other step.
 */
export function stepSeries(run: Pick<RunState, 'seriesDocuments'>, step: Step): StepSeries[] {
  if (step.op?.op !== 'read_series') return []
  const byDocument = new Map<string, string[]>()
  for (const tag of step.op.tags) {
    const documentId = run.seriesDocuments?.[tag]
    if (!documentId) continue
    const tags = byDocument.get(documentId) ?? []
    if (!tags.includes(tag) && tags.length < LIVE_CARD_MAX_TAGS) tags.push(tag)
    byDocument.set(documentId, tags)
  }
  return [...byDocument].map(([documentId, tags]) => ({ documentId, tags }))
}

export const liveCardId = (series: StepSeries): string => `${series.documentId}|${series.tags.join(',')}`

/** Whether a card for exactly this series is already on the run. */
export const hasLiveCard = (cards: LiveCardSpec[], series: StepSeries): boolean =>
  cards.some((c) => c.id === liveCardId(series))

const storageKey = (runId: string): string => `workflow.liveCards.v1.${runId}`

/** The run's saved cards; none when nothing is saved, the storage is unavailable or the value is not ours. */
export function loadLiveCards(runId: string): LiveCardSpec[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(storageKey(runId)) ?? '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (c): c is LiveCardSpec =>
        typeof c?.id === 'string' &&
        typeof c.documentId === 'string' &&
        typeof c.title === 'string' &&
        (c.stepId === undefined || typeof c.stepId === 'string') &&
        Array.isArray(c.tags) &&
        c.tags.every((t: unknown) => typeof t === 'string')
    )
  } catch {
    return []
  }
}

function saveLiveCards(runId: string, cards: LiveCardSpec[]): void {
  try {
    if (cards.length) window.localStorage.setItem(storageKey(runId), JSON.stringify(cards))
    else window.localStorage.removeItem(storageKey(runId))
  } catch {
    // Storage full or blocked: the cards stay for this visit and are not remembered.
  }
}

/**
 * The live cards of one run, kept in this browser (localStorage, per run id) so they survive a
 * reload. There is no per-run store for page furniture on the server, and a card is only a view
 * of a document, so nothing is shared with other users or devices.
 */
export function useRunLiveCards(runId: string | null) {
  const [cards, setCards] = useState<LiveCardSpec[]>(() => (runId ? loadLiveCards(runId) : []))

  useEffect(() => {
    setCards(runId ? loadLiveCards(runId) : [])
  }, [runId])

  const update = useCallback(
    (change: (cards: LiveCardSpec[]) => LiveCardSpec[]) => {
      if (!runId) return
      setCards((current) => {
        const next = change(current)
        saveLiveCards(runId, next)
        return next
      })
    },
    [runId]
  )

  const add = useCallback(
    (series: StepSeries, title: string, stepId?: string) =>
      update((current) => {
        const id = liveCardId(series)
        return current.some((c) => c.id === id) ? current : [...current, { id, ...series, title, ...(stepId ? { stepId } : {}) }]
      }),
    [update]
  )
  const remove = useCallback((id: string) => update((current) => current.filter((c) => c.id !== id)), [update])

  return { cards, add, remove }
}
